import React, { useState, useRef, useCallback } from 'react';
import './App.css';

interface QuadrantData {
  x: number;
  y: number;
  w: number;
  h: number;
}

const App: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [originalImageSrc, setOriginalImageSrc] = useState<string>('');
  const [processedCanvas, setProcessedCanvas] = useState<HTMLCanvasElement | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultCanvasRef = useRef<HTMLCanvasElement>(null);
  const quadrantCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([null, null, null, null]);
  const swappedCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([null, null, null, null]);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('请选择有效的图片文件！');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setOriginalImageSrc(e.target?.result as string);
        setShowResults(true);
        showQuadrants(img);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const showQuadrants = (img: HTMLImageElement) => {
    // 使用setTimeout确保DOM已经渲染完成
    setTimeout(() => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const halfWidth = Math.floor(width / 2);
      const halfHeight = Math.floor(height / 2);

      // 创建临时canvas来绘制原图
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;
      
      tempCtx.imageSmoothingEnabled = false;
      tempCtx.drawImage(img, 0, 0);

      // 绘制四个象限
      const quadrants: QuadrantData[] = [
        { x: 0, y: 0, w: halfWidth, h: halfHeight }, // 左上
        { x: halfWidth, y: 0, w: width - halfWidth, h: halfHeight }, // 右上
        { x: 0, y: halfHeight, w: halfWidth, h: height - halfHeight }, // 左下
        { x: halfWidth, y: halfHeight, w: width - halfWidth, h: height - halfHeight } // 右下
      ];

      quadrants.forEach((quad, index) => {
        const canvas = quadrantCanvasRefs.current[index];
        if (!canvas) {
          console.log(`Canvas ${index} not found`);
          return;
        }
        
        canvas.width = quad.w;
        canvas.height = quad.h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(
          tempCanvas,
          Math.floor(quad.x), Math.floor(quad.y), Math.floor(quad.w), Math.floor(quad.h),
          0, 0, Math.floor(quad.w), Math.floor(quad.h)
        );
      });
    }, 100);
  };

  const processImage = async () => {
    if (!originalImage) return;

    setIsProcessing(true);

    // 使用setTimeout来模拟异步处理，让UI有时间更新
    setTimeout(() => {
      const img = originalImage;
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const halfWidth = Math.floor(width / 2);
      const halfHeight = Math.floor(height / 2);

      // 创建结果canvas
      const resultCanvas = resultCanvasRef.current;
      if (!resultCanvas) return;
      
      resultCanvas.width = width;
      resultCanvas.height = height;
      const ctx = resultCanvas.getContext('2d');
      if (!ctx) return;

      // 禁用图像平滑以避免模糊和白线
      ctx.imageSmoothingEnabled = false;

      // 创建临时canvas来绘制原图
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;
      
      tempCtx.imageSmoothingEnabled = false;
      tempCtx.drawImage(img, 0, 0);

      // 象限定义 - 确保完美拼接，避免白色线条
      const quadrants: QuadrantData[] = [
        { x: 0, y: 0, w: halfWidth, h: halfHeight }, // 左上
        { x: halfWidth, y: 0, w: width - halfWidth, h: halfHeight }, // 右上
        { x: 0, y: halfHeight, w: halfWidth, h: height - halfHeight }, // 左下
        { x: halfWidth, y: halfHeight, w: width - halfWidth, h: height - halfHeight } // 右下
      ];

      // 交换对角线象限：
      // 原左上(0) → 新右下(3)
      // 原右上(1) → 新左下(2)  
      // 原左下(2) → 新右上(1)
      // 原右下(3) → 新左上(0)
      const swapMapping = [3, 2, 1, 0];

      swapMapping.forEach((sourceIndex, targetIndex) => {
        const source = quadrants[sourceIndex];
        const target = quadrants[targetIndex];
        
        // 使用整数坐标避免亚像素渲染导致的白线
        ctx.drawImage(
          tempCanvas,
          Math.floor(source.x), Math.floor(source.y), Math.floor(source.w), Math.floor(source.h),
          Math.floor(target.x), Math.floor(target.y), Math.floor(target.w), Math.floor(target.h)
        );
      });

      // 显示交换后的象限预览
      swapMapping.forEach((sourceIndex, targetIndex) => {
        const canvas = swappedCanvasRefs.current[targetIndex];
        if (!canvas) return;
        
        const source = quadrants[sourceIndex];
        
        canvas.width = source.w;
        canvas.height = source.h;
        const quadCtx = canvas.getContext('2d');
        if (!quadCtx) return;
        
        quadCtx.imageSmoothingEnabled = false;
        quadCtx.drawImage(
          tempCanvas,
          Math.floor(source.x), Math.floor(source.y), Math.floor(source.w), Math.floor(source.h),
          0, 0, Math.floor(source.w), Math.floor(source.h)
        );
      });

      setProcessedCanvas(resultCanvas);
      setIsProcessing(false);
    }, 100);
  };

  const downloadImage = () => {
    if (!processedCanvas) return;

    processedCanvas.toBlob((blob) => {
      if (!blob) return;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'swapped_quadrants_image.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="app">
      <div className="container">
        <h1>🖼️ 图片2x2分割交换工具</h1>
        
        <div className="explanation">
          <h4>功能说明：</h4>
          <p>此工具将图片分割成2x2的四个象限，然后交换对角线上的象限：<br />
          • 左上角 ↔ 右下角<br />
          • 右上角 ↔ 左下角</p>
        </div>
        
        <div 
          className={`upload-area ${isDragOver ? 'dragover' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="upload-icon">📁</div>
          <div className="upload-text">点击选择图片或拖拽图片到此处</div>
          <button className="btn">选择文件</button>
          <input 
            type="file" 
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </div>
        
        <button 
          className="btn process-btn" 
          onClick={processImage}
          disabled={!originalImage || isProcessing}
        >
          🔄 处理图片
        </button>
        
        {showResults && (
          <div className="image-display">
            <div className="image-section">
              <h3>原始图片</h3>
              <div className="image-container">
                <img src={originalImageSrc} alt="原始图片" className="preview-image" />
              </div>
              <h4>象限分布：</h4>
              <div className="quadrant-grid">
                <div className="quadrant">
                  <canvas ref={el => { quadrantCanvasRefs.current[0] = el; }}></canvas>
                  <div className="quadrant-label">左上</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { quadrantCanvasRefs.current[1] = el; }}></canvas>
                  <div className="quadrant-label">右上</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { quadrantCanvasRefs.current[2] = el; }}></canvas>
                  <div className="quadrant-label">左下</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { quadrantCanvasRefs.current[3] = el; }}></canvas>
                  <div className="quadrant-label">右下</div>
                </div>
              </div>
            </div>
            
            <div className="image-section">
              <h3>处理后图片</h3>
              <div className="image-container">
                <canvas ref={resultCanvasRef} className="preview-image"></canvas>
              </div>
              <h4>交换后象限：</h4>
              <div className="quadrant-grid">
                <div className="quadrant">
                  <canvas ref={el => { swappedCanvasRefs.current[0] = el; }}></canvas>
                  <div className="quadrant-label">右下→左上</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { swappedCanvasRefs.current[1] = el; }}></canvas>
                  <div className="quadrant-label">左下→右上</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { swappedCanvasRefs.current[2] = el; }}></canvas>
                  <div className="quadrant-label">右上→左下</div>
                </div>
                <div className="quadrant">
                  <canvas ref={el => { swappedCanvasRefs.current[3] = el; }}></canvas>
                  <div className="quadrant-label">左上→右下</div>
                </div>
              </div>
              {processedCanvas && (
                <button className="btn download-btn" onClick={downloadImage}>
                  💾 下载处理后的图片
                </button>
              )}
            </div>
          </div>
        )}
        
        {isProcessing && (
          <div className="loading">
            处理中，请稍候...
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
